// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import { black, gray04, primary } from 'presentations/views/styles/color';
import Icon from 'presentations/views/components/atoms/Icon';
import { spacing, fontSize, fontWeight } from 'presentations/views/styles';
import { SyntheticEvent, useContext } from 'react';
import PlanUpgradeDialog from 'presentations/views/components/organisms/PlanUpgradeDialog';
import { PopoverOrigin } from '@mui/material/Popover';
import Popover from 'presentations/views/components/atoms/Popover';
import Link from 'presentations/views/components/atoms/Link';
import Image from 'presentations/views/components/atoms/Image';
import {
  dashboardEventTypeCalendarPath,
  dashboardCalendarsPath,
  hostEventTypeNewPath,
  EventCreateViewType,
  formNewPath,
  webPath,
  adminUserListPath,
  dashboardEventTypePollPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import { getDateTime } from 'infras/datetime';
import { ViewerItem } from 'presentations/api/schema/user';
import Button from 'presentations/views/components/atoms/Button';
import { trackClickEvent } from 'presentations/views/helpers/analytics';

interface Props {
  onCloseMenu?: () => void;
  anchor: HTMLElement | null;
  isShowingPlanUpgrade?: boolean;
  onClosePlanUpgrade?: () => void;
  onClickUserInvite?: () => void;
  onClickNewEventType?: (e: SyntheticEvent<HTMLAnchorElement>) => void;
  onClickNewForm?: (e: SyntheticEvent<HTMLAnchorElement>) => void;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  viewer: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const from = router.asPath;

  const onClickMenu = (e: SyntheticEvent<HTMLAnchorElement>) => {
    if (props.onCloseMenu) props.onCloseMenu();
    trackClickEvent(e);
  };

  const onClickUserInvite = (e: SyntheticEvent<HTMLAnchorElement>) => {
    window.open(webPath(adminUserListPath(), router.locale));
    onClickMenu(e);
  };

  return (
    <>
      <StyledPopover
        onClose={props.onCloseMenu}
        anchor={props.anchor}
        anchorOrigin={props.anchorOrigin}
        transformOrigin={props.transformOrigin}
      >
        <Row>
          <ListItem
            href={hostEventTypeNewPath(null, from)}
            onClick={props.onClickNewEventType}
          >
            <Inner>
              <IconContainer>
                <ListItemIcon name="calendar-clock" />
              </IconContainer>
              <ListItemContent>
                <ListItemTitle>{t('Create Event Type')}</ListItemTitle>
                <ListItemTextitle>
                  {t('Create Event Type desc')}
                </ListItemTextitle>
              </ListItemContent>
            </Inner>
          </ListItem>
          <ListItem
            href={dashboardEventTypeCalendarPath(from)}
            onClick={trackClickEvent}
            dataProps={{ ['data-event-name']: 'create menu click oneoff' }}
          >
            <Inner>
              <IconContainer>
                <ListItemIcon name="calendar-cursor" />
              </IconContainer>
              <ListItemContent>
                <ListItemTitle>{t('Create One off')}</ListItemTitle>
                <ListItemTextitle>{t('Create One off desc')}</ListItemTextitle>
              </ListItemContent>
            </Inner>
          </ListItem>
          <ListItem
            onClick={trackClickEvent}
            dataProps={{ ['data-event-name']: 'create menu click poll' }}
            href={dashboardEventTypePollPath(from)}
          >
            <Inner>
              <IconContainer>
                <ListItemIcon name="poll" />
              </IconContainer>
              <ListItemContent>
                <ListItemTitle>{t('Create Poll')}</ListItemTitle>
                <ListItemTextitle>{t('Create Poll desc')}</ListItemTextitle>
              </ListItemContent>
            </Inner>
          </ListItem>
          <ListItem
            href={formNewPath(null, from)}
            onClick={props.onClickNewForm}
          >
            <Inner>
              <IconContainer>
                <ListItemIcon name="form-select" />
              </IconContainer>
              <ListItemContent>
                <ListItemTitle>{t('Create Routing Form')}</ListItemTitle>
                <ListItemTextitle>
                  {t('Create Routing Form desc')}
                </ListItemTextitle>
              </ListItemContent>
            </Inner>
          </ListItem>
          {/* <ListItem
            onClick={onClickMenu}
            href={createPath(EventCreateViewType.Calendar)}
            dataProps={{
              ['data-event-name']: 'create menu click event calendar',
            }}
          >
            <Inner>
              <IconContainer>
                <ListItemIcon name="calendar-plus" />
              </IconContainer>
              <ListItemContent>
                <ListItemTitle>{t('Create Event')}</ListItemTitle>
                <ListItemTextitle>{t('Create Event desc')}</ListItemTextitle>
              </ListItemContent>
            </Inner>
          </ListItem> */}
          <InviteContainer>
            <InviteButton
              color="primary"
              onClick={onClickUserInvite}
              dataProps={{
                ['data-event-name']: 'create menu click user invite',
              }}
            >
              {t('Invite Members')}
            </InviteButton>
          </InviteContainer>
        </Row>
      </StyledPopover>
      {props.isShowingPlanUpgrade && props.onClosePlanUpgrade && (
        <PlanUpgradeDialog onClose={props.onClosePlanUpgrade} />
      )}
    </>
  );
}

const ListItem = styled(Link)`
  /* flex: 1; */
  border-bottom: 1px solid ${gray04};
  padding: ${spacing(12)};
  color: ${black};
  :hover {
    color: ${primary};
  }
`;
const Inner = styled.div`
  display: flex;
  justify-content: flex-start;
`;
const IconContainer = styled.div`
  width: 28px;
`;
const ListItemIcon = styled(Icon)`
  width: 24px !important;
  height: 24px !important;
  color: ${primary};
`;
const ListItemContent = styled.div`
  flex: 1;
  width: 280px;
  margin-left: ${spacing(4)};
`;
const ListItemTitle = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
`;
const ListItemTextitle = styled.div`
  font-size: ${fontSize(13)};
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${spacing(8)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const InviteButton = styled(Button)`
  width: 100%;
`;
const InviteContainer = styled.div`
  padding: ${spacing(16)} ${spacing(8)};
`;
const AssistantIcon = styled(Image)`
  width: 24px;
  height: 24px;
`;
const StyledPopover = styled(Popover)``;
